import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowRight, Play, TrendingUp } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CertificateCard, ReadinessBadge } from "../components/Cards";

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [data, setData] = useState<any>(null);
  const [loadError, setLoadError] = useState(false);
  const fr = i18n.language === "fr";

  useEffect(() => {
    api.get("/learn/dashboard").then((r) => setData(r.data)).catch(() => setLoadError(true));
  }, []);

  if (loadError) return <p className="text-[#FF3B30]" data-testid="dashboard-error">{t("common.error")}</p>;
  if (!data) return <p className="text-muted-foreground">{t("common.loading")}</p>;
  const { next_action, journey, certifications, readiness } = data;

  return (
    <div className="space-y-8" data-testid="learner-dashboard">
      <div className="fade-up">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{t("dashboard.nextAction")}</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-2">{t("dashboard.title")}</h1>
      </div>

      <div className="fade-up-1">
        {next_action ? (
          <div data-testid="next-action-card" className="bg-[#002BF6] text-white rounded-md p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-6 justify-between">
            <div>
              <p className="text-xs font-mono-v uppercase tracking-wider text-white/60">
                {next_action.type === "CONTINUE_COURSE" ? t("dashboard.continueCourse") : t("dashboard.enrollCourse")}
              </p>
              <h2 className="font-heading text-xl sm:text-2xl font-semibold mt-1">
                {fr ? next_action.title_fr : next_action.title_en}
              </h2>
              {next_action.type === "CONTINUE_COURSE" && (
                <div className="mt-3 flex items-center gap-3">
                  <div className="w-40 h-1.5 bg-white/25 rounded-full overflow-hidden">
                    <div className="h-full bg-white rounded-full" style={{ width: `${next_action.progress_pct}%` }} />
                  </div>
                  <span className="text-sm font-mono-v">{next_action.progress_pct}%</span>
                </div>
              )}
              {next_action.status === "CONTENT_REQUIRED" && (
                <p className="mt-2 text-xs text-white/70 font-mono-v">{t("dashboard.contentRequired")}</p>
              )}
            </div>
            <Link to={`/courses/${next_action.course_id}`} data-testid="next-action-cta"
              className="shrink-0 inline-flex items-center gap-2 bg-white text-[#002BF6] px-5 py-3 rounded-md font-medium hover:bg-white/90 transition-colors duration-200">
              <Play size={16} />
              {next_action.type === "CONTINUE_COURSE" ? t("player.resume") : t("player.start")}
              <ArrowRight size={16} />
            </Link>
          </div>
        ) : (
          <div className="swiss-card p-8 text-muted-foreground">{t("dashboard.noAction")}</div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 fade-up-2">
        <div className="swiss-card p-6" data-testid="journey-summary-card">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-2">
            <TrendingUp size={13} /> {t("dashboard.journeyTitle")}
          </p>
          <div className="mt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t("dashboard.currentStage")}</span>
              <span className="font-mono-v font-semibold" data-testid="current-stage-value">{journey.current_stage}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t("dashboard.nextStage")}</span>
              <span className="font-mono-v">{journey.next_stage || "—"}</span>
            </div>
          </div>
          <Link to="/journey" data-testid="view-journey-link" className="mt-4 inline-flex items-center gap-1 text-sm text-[#002BF6] font-medium hover:underline">
            {t("dashboard.viewJourney")} <ArrowRight size={14} />
          </Link>
        </div>

        <div className="swiss-card p-6 md:col-span-2" data-testid="readiness-panel">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{t("dashboard.readiness")}</p>
          <div className="mt-4 space-y-3">
            {readiness.map((r: any) => (
              <div key={r.campaign_key} className="flex items-center justify-between gap-3 border-b border-border pb-2 last:border-0" data-testid={`readiness-row-${r.campaign_key}`}>
                <span className="text-sm font-medium">{fr ? r.campaign_title_fr : r.campaign_title_en}</span>
                <ReadinessBadge status={r.readiness_status} />
              </div>
            ))}
          </div>
          <p className="mt-3 text-[11px] text-muted-foreground">{t("readiness.noExternal")}</p>
        </div>
      </div>

      <div className="fade-up-3">
        <h3 className="font-heading text-lg font-semibold mb-3">{t("dashboard.certifications")}</h3>
        {certifications.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="no-certs-message">{t("dashboard.noCerts")}</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {certifications.map((c: any) => <CertificateCard key={c.id} cert={c} learnerName={user ? (user as any).name : ""} />)}
          </div>
        )}
      </div>

      <div>
        <h3 className="font-heading text-lg font-semibold mb-3">{t("dashboard.recommended")}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {journey.recommended_courses.map((c: any) => (
            <Link to={`/courses/${c.id}`} key={c.id} data-testid={`recommended-course-${c.slug}`} className="swiss-card p-6 block">
              <span className="text-[10px] font-mono-v px-2 py-0.5 border border-border rounded-md text-muted-foreground">
                {t(`courses.status.${c.status}`)}
              </span>
              <h4 className="font-heading font-semibold mt-3">{fr ? c.title_fr : c.title_en}</h4>
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{fr ? c.description_fr : c.description_en}</p>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
